/**
 * React Query hooks for LLM provider-selection policy (ADR-042).
 *
 * Endpoints:
 *   GET    /api/v1/admin/dr/{dr_code}/llm-policy              (non-tenant)
 *   PATCH  /api/v1/admin/dr/{dr_code}/llm-policy              (non-tenant)
 *   GET    /api/v1/tenants/{current}/llm-policy               (tenant-scoped)
 *   PATCH  /api/v1/tenants/{current}/llm-policy
 *   POST   /api/v1/tenants/{current}/llm-policy/catalog-overrides
 *   DELETE /api/v1/tenants/{current}/llm-policy/catalog-overrides/{slug}
 *   POST   /api/v1/tenants/{current}/llm-policy/simulate
 *
 * The tenant-aware api-client auto-prefixes tenant-scoped paths with
 * `/api/v1/tenants/{currentTenant}/`, so callers pass relative paths
 * like `/llm-policy`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type {
  CatalogOverrideUpsert,
  DrLlmPolicy,
  DrLlmPolicyUpdate,
  EffectivePolicy,
  SimulatePolicyRequest,
  SimulatePolicyResponse,
  TenantLlmPolicy,
  TenantLlmPolicyUpdate,
} from '../types/policy';

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const policyKeys = {
  all: ['llm-policy'] as const,
  dr: (drCode: string) => ['llm-policy', 'dr', drCode] as const,
  tenant: ['llm-policy', 'tenant'] as const,
  tenantEffective: (drCode: string, catalogSlug?: string) =>
    [
      'llm-policy',
      'tenant',
      'effective',
      drCode,
      catalogSlug ?? null,
    ] as const,
};

// ---------------------------------------------------------------------------
// DR-level policy (SystemAdmin)
// ---------------------------------------------------------------------------

export function useDrLlmPolicy(drCode: string) {
  return useQuery({
    queryKey: policyKeys.dr(drCode),
    queryFn: async () => {
      const { data } = await apiClient.get<DrLlmPolicy>(
        `/api/v1/admin/dr/${drCode}/llm-policy`,
      );
      return data;
    },
    enabled: !!drCode,
  });
}

export function useUpdateDrLlmPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      drCode,
      update,
    }: {
      drCode: string;
      update: DrLlmPolicyUpdate;
    }) => {
      const { data } = await apiClient.patch<DrLlmPolicy>(
        `/api/v1/admin/dr/${drCode}/llm-policy`,
        update,
      );
      return data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: policyKeys.dr(vars.drCode) });
      qc.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant policy — stored values (NULL = inherit)
// ---------------------------------------------------------------------------

export function useTenantLlmPolicy() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: [...policyKeys.tenant, tenantId],
    // El cliente tenant-aware necesita el tenant antes de construir la URL;
    // sin él la petición saldría sin prefijo y el SPA devolvería index.html.
    enabled: !!tenantId,
    queryFn: async (): Promise<TenantLlmPolicy | null> => {
      // El endpoint tenant devuelve `{policy: ...}` (y `effective` con
      // `?explain=true`). El formulario consume la política almacenada.
      const { data } = await apiClient.get<
        { policy?: TenantLlmPolicy | null } | TenantLlmPolicy
      >('/llm-policy');
      if (data && typeof data === 'object' && 'policy' in data) {
        return (data as { policy?: TenantLlmPolicy | null }).policy ?? null;
      }
      return data as TenantLlmPolicy;
    },
  });
}

// ---------------------------------------------------------------------------
// Tenant effective policy with source_trace (?explain=true)
// ---------------------------------------------------------------------------

export function useTenantEffectivePolicy(params: {
  drCode: string;
  catalogSlug?: string;
  enabled?: boolean;
}) {
  const { drCode, catalogSlug, enabled = true } = params;
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;
  return useQuery({
    queryKey: policyKeys.tenantEffective(drCode, catalogSlug),
    queryFn: async () => {
      const search = new URLSearchParams({
        explain: 'true',
        dr_code: drCode,
      });
      if (catalogSlug) search.set('catalog_slug', catalogSlug);
      const { data } = await apiClient.get<{ effective: EffectivePolicy }>(
        `/llm-policy?${search}`,
      );
      return data.effective;
    },
    enabled: enabled && !!drCode && !!tenantId,
  });
}

// ---------------------------------------------------------------------------
// Tenant policy mutations
// ---------------------------------------------------------------------------

export function useUpdateTenantLlmPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      drCode,
      update,
      force = false,
    }: {
      drCode: string;
      update: TenantLlmPolicyUpdate;
      force?: boolean;
    }) => {
      const search = new URLSearchParams({ dr_code: drCode });
      if (force) search.set('force', 'true');
      const { data } = await apiClient.patch<TenantLlmPolicy>(
        `/llm-policy?${search}`,
        update,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}

export function useUpsertCatalogOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      drCode,
      override,
      force = false,
    }: {
      drCode: string;
      override: CatalogOverrideUpsert;
      force?: boolean;
    }) => {
      const search = new URLSearchParams({ dr_code: drCode });
      if (force) search.set('force', 'true');
      const { data } = await apiClient.post<TenantLlmPolicy>(
        `/llm-policy/catalog-overrides?${search}`,
        override,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}

export function useDeleteCatalogOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (catalogSlug: string) => {
      await apiClient.delete(`/llm-policy/catalog-overrides/${catalogSlug}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: policyKeys.all });
    },
  });
}

export function useSimulateTenantLlmPolicy() {
  return useMutation({
    mutationFn: async (body: SimulatePolicyRequest) => {
      const { data } = await apiClient.post<SimulatePolicyResponse>(
        '/llm-policy/simulate',
        body,
      );
      return data;
    },
  });
}
